import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import { FaCheckCircle, FaCog, FaTachometerAlt, FaTools } from "react-icons/fa";
import "./Diagnostic.css";
import heroImage from "../assets/ds.webp";
import Footer from "../components/Footer";

const Diagnostic = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const navigate = useNavigate();

  const servicess = [
    { id: 1, name: "OBD-II Scan (Computer Scan)", description: "Reading engine error codes, checking fault codes, live data monitoring", category: "Computer Diagnostics" },
    { id: 2, name: "Engine System Check", description: "Ignition system, fuel delivery, air intake, emissions system performance", category: "Engine Systems" },
    { id: 3, name: "Transmission System Diagnosis", description: "Transmission error codes, shift timing data, clutch/solenoid performance", category: "Transmission Systems" },
    { id: 4, name: "ABS & Brake System Scan", description: "ABS module errors, wheel speed sensor readings, brake pressure data", category: "Safety Systems" },
    { id: 5, name: "Airbag / SRS System Scan", description: "Airbag module faults, pretensioner issues, crash data analysis", category: "Safety Systems" },
    { id: 6, name: "Battery & Charging System Test", description: "Battery health & voltage, alternator output, starter motor performance", category: "Electrical Systems" },
    { id: 7, name: "Cooling System Check", description: "Coolant temperature readings, thermostat operation, radiator fan performance", category: "Engine Systems" },
    { id: 8, name: "Fuel & Exhaust System Diagnosis", description: "Fuel pressure testing, emissions readiness, exhaust flow/sensor issues", category: "Engine Systems" },
    { id: 9, name: "Suspension & Steering Scan", description: "Electronic suspension fault codes, power steering module scan (EPS)", category: "Chassis Systems" },
    { id: 10, name: "Body Control Module (BCM) Scan", description: "Central locking, lights, AC/climate control, instrument cluster issues", category: "Body Electronics" },
    { id: 11, name: "Wiring & Electrical System Check", description: "Visual inspection, continuity testing, voltage drop analysis, circuit tracing", category: "Electrical Systems" },
  ];

  const handleToggle = (service) => {
    const isSelected = selectedServices.find((s) => s.id === service.id);
    if (isSelected) setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
    else setSelectedServices([...selectedServices, service]);
  };

  const handleRequestDiagnostic = () => {
    if (selectedServices.length === 0) return;

    const serviceNames = selectedServices.map(service => service.name).join(", ");
    const subject = `Vehicle Diagnostic Service Request - ${serviceNames}`;
    const message = `Hello,\n\nI am interested in the following diagnostic services:\n\n${selectedServices.map(service => `• ${service.name} (${service.category})\n  ${service.description}`).join('\n\n')}\n\nWhat I'm looking for in these selected diagnostic services:\n- Comprehensive system scanning and analysis\n- Fault code identification and interpretation\n- Detailed diagnostic reports\n- Professional troubleshooting recommendations\n\nPlease provide me with information about the diagnostic process, pricing, and availability.\n\nThank you!`;

    navigate("/contact", { state: { subject, message } });
  };

  const canonical = "https://carforensic.uk/diagnostic";

  const diagnosticSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Vehicle Diagnostics",
    "url": canonical,
    "areaServed": ["Kent", "Maidstone", "Gillingham", "Sevenoaks", "Sittingbourne"],
    "provider": {
      "@type": "AutoRepair",
      "name": "Car Forensics LTD",
      "url": "https://carforensic.uk/",
      "telephone": "+447501911970",
      "email": "carforensicsltd@gmail.com"
    },
    "description":
      "Advanced mobile vehicle diagnostics and troubleshooting across Kent including Maidstone, Gillingham, Sevenoaks and Sittingbourne."
  };

  return (
    <>
      <SEO
        title="Car Forensics LTD | Diagnostics"
        description="Advanced vehicle diagnostics and troubleshooting across Kent — including Maidstone, Gillingham, Sevenoaks and Sittingbourne. Choose the diagnostic checks you need and request support from Car Forensics LTD."
        canonical={canonical}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(diagnosticSchema)}
        </script>
      </Helmet>

      <div className="diagnostic-page">
        <section className="diagnostic-hero" style={{ backgroundImage: `url(${heroImage})` }}>
          <div className="heros-overlay"></div>
          <div className="diagnostic-hero-content">
            <div className="hero-icon-wrapper">
              <FaCog className="hero-icon rotating" />
            </div>
            <h1>Vehicle Diagnostic Services</h1>
            <p>Advanced diagnostic and troubleshooting for all vehicle systems</p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaTachometerAlt />
                <span>Complete System Scan</span>
              </div>
              <div className="stat-item">
                <FaTools />
                <span>Professional Equipment</span>
              </div>
              <div className="stat-item">
                <FaCheckCircle />
                <span>Expert Analysis</span>
              </div>
            </div>
          </div>
        </section>

        <section className="diagnostic-services-section">
          <div className="diagnostic-container">
            <div className="section-intro">
              <h2>Select Your Required Diagnostic Services</h2>
              <p>Choose from our comprehensive range of vehicle diagnostic and troubleshooting services.</p>
            </div>

            <div className="diagnostic-content-wrapper">
              <div className="servicess-selection-area">

                <div className="service-category-section">
                  <h3 className="category-heading">Computer & Engine Diagnostics</h3>
                  <div className="services-grid">
                    {servicess
                      .filter(service => ["Computer Diagnostics", "Engine Systems"].includes(service.category))
                      .map((service) => {
                        const isSelected = selectedServices.find((s) => s.id === service.id);
                        return (
                          <label key={service.id} className={`service-checkbox-card ${isSelected ? "selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(service)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="servicess-info">
                              <div className="servicess-details-wrapper">
                                <span className="servicess-name">{service.name}</span>
                                <span className="servicess-category-tag">{service.category}</span>
                              </div>
                              <p className="servicess-description">{service.description}</p>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>

                <div className="service-category-section">
                  <h3 className="category-heading">Safety & Electrical Systems</h3>
                  <div className="services-grid">
                    {servicess
                      .filter(service => ["Safety Systems", "Electrical Systems"].includes(service.category))
                      .map((service) => {
                        const isSelected = selectedServices.find((s) => s.id === service.id);
                        return (
                          <label key={service.id} className={`service-checkbox-card ${isSelected ? "selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(service)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="servicess-info">
                              <div className="servicess-details-wrapper">
                                <span className="servicess-name">{service.name}</span>
                                <span className="servicess-category-tag">{service.category}</span>
                              </div>
                              <p className="servicess-description">{service.description}</p>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>

                <div className="service-category-section">
                  <h3 className="category-heading">Other Vehicle Systems</h3>
                  <div className="services-grid">
                    {servicess
                      .filter(service => ["Transmission Systems", "Chassis Systems", "Body Electronics"].includes(service.category))
                      .map((service) => {
                        const isSelected = selectedServices.find((s) => s.id === service.id);
                        return (
                          <label key={service.id} className={`service-checkbox-card ${isSelected ? "selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(service)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="servicess-info">
                              <div className="servicess-details-wrapper">
                                <span className="servicess-name">{service.name}</span>
                                <span className="servicess-category-tag">{service.category}</span>
                              </div>
                              <p className="servicess-description">{service.description}</p>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>
              </div>

              <div className="summary-sidebar">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FaCheckCircle className="summary-icon" />
                    Selected Services
                  </h3>

                  {selectedServices.length === 0 ? (
                    <div className="empty-state">
                      <p>No services selected yet</p>
                      <span>Select services from the left to see your selection</span>
                    </div>
                  ) : (
                    <>
                      <div className="selected-services-list">
                        {selectedServices.map((service) => (
                          <div key={service.id} className="selected-service-item">
                            <div className="service-detail">
                              <span className="item-name">{service.name}</span>
                              <span className="item-category">{service.category}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="summary-divider"></div>

                      <div className="service-count-section">
                        <div className="count-row">
                          <span>Total Selected Services</span>
                          <span className="count-number">{selectedServices.length}</span>
                        </div>
                      </div>

                      <button className="cta-button-diagnostic" onClick={handleRequestDiagnostic}>
                        <span>Request Diagnostic</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <div className="info-note">
                        <p>Diagnostic services will help identify and troubleshoot vehicle issues. Contact us for detailed analysis.</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="contact-quick-card">
                  <h4>Need Assistance?</h4>
                  <p>Our experts are here to help</p>
                  <a href="tel:+447501911970" className="phone-link-diagnostic">
                    📞 +447501911970
                  </a>

                  {/* internal link fixed */}
                  <Link to="/contact" className="contact-link-diagnostic">
                    Contact Us
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </section>

        <section className="diagnostic-info-section">
          <div className="diagnostic-container">
            <div className="infoss-grid">
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaCheckCircle />
                </div>
                <h3>Complete Analysis</h3>
                <p>Comprehensive diagnostic scanning across all vehicle systems and modules.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTachometerAlt />
                </div>
                <h3>Advanced Tools</h3>
                <p>State-of-the-art diagnostic equipment for accurate fault detection and analysis.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTools />
                </div>
                <h3>Expert Diagnosis</h3>
                <p>Skilled technicians providing detailed reports and repair recommendations.</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Diagnostic;
